import React from "react";
import { FieldArray, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardBody,
  Col,
  FormFeedback,
  Input,
  Label,
  Row,
} from "reactstrap";

import {
  BuilderQuestion,
  BuilderQuestionType,
} from "../../pages/Adatis/Studio/Settings/AnamnesisTemplates";
import {
  ANAMNESIS_TEXTAREA_BUILDER_ROWS,
  ANAMNESIS_TEXTAREA_MAX_CHARS,
} from "../../services/clientRecordTemplateService";
import { ConsentTerm } from "../../services/consentTermService";

const QUESTION_TYPES: BuilderQuestionType[] = [
  "text",
  "textarea",
  "boolean",
  "select",
  "section",
  "consent_text",
  "consent_checkbox",
  "signature",
];

export const emptyBuilderQuestion = (): BuilderQuestion => ({
  label: "",
  type: "text",
  required: false,
  options: "",
  body: "",
  clinical_gate: false,
  clinical_gate_service_ids: [],
  clinical_gate_product_ids: [],
});

type CatalogOption = { id: number; name: string };

type AnamnesisTemplateBuilderFieldsProps = {
  getQuestionError: (index: number, field: keyof BuilderQuestion) => string | undefined;
  consentTerms?: ConsentTerm[];
  serviceOptions?: CatalogOption[];
  productOptions?: CatalogOption[];
};

const AnamnesisTemplateBuilderFields = ({
  getQuestionError,
  consentTerms = [],
  serviceOptions = [],
  productOptions = [],
}: AnamnesisTemplateBuilderFieldsProps) => {
  const { t } = useTranslation();
  const formik = useFormikContext<{ questions: BuilderQuestion[] }>();

  const toggleIdInList = (
    fieldPath: string,
    current: number[] | undefined,
    id: number,
    checked: boolean
  ) => {
    const base = Array.isArray(current) ? current : [];
    const next = checked
      ? Array.from(new Set([...base, id]))
      : base.filter((item) => item !== id);
    formik.setFieldValue(fieldPath, next);
  };

  const questionTypeLabel = (type: BuilderQuestionType): string =>
    t(`studio.anamnesis.builder.type_${type}`);

  const questionsArrayError =
    typeof formik.errors.questions === "string" ? formik.errors.questions : undefined;

  const applyLibraryTerm = (index: number, termId: string) => {
    const term = consentTerms.find((item) => String(item.id) === termId);
    if (!term) {
      return;
    }

    formik.setFieldValue(`questions.${index}.label`, term.title);
    formik.setFieldValue(`questions.${index}.body`, term.body_text);
  };

  return (
    <div className="mb-3">
      <Label className="mb-2">{t("studio.anamnesis.builder.section_title")}</Label>
      <p className="text-muted small mb-3">
        {t("studio.anamnesis.builder.protocol_space_hint")}
      </p>

      <FieldArray name="questions">
        {({ push, remove }) => (
          <>
            {formik.values.questions.length === 0 ? (
              <p className="text-muted small mb-3">{t("studio.anamnesis.builder.empty")}</p>
            ) : (
              formik.values.questions.map((question, index) => (
                <Card key={`question-${index}`} className="border shadow-none mb-3">
                  <CardBody className="p-3">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <span className="badge bg-light text-body">
                        {question.type === "section"
                          ? t("studio.anamnesis.builder.section_number", {
                              number: index + 1,
                            })
                          : t("studio.anamnesis.builder.question_number", {
                              number: index + 1,
                            })}
                      </span>
                      <Button
                        color="soft-danger"
                        size="sm"
                        type="button"
                        title={t("studio.anamnesis.builder.remove_question")}
                        onClick={() => remove(index)}
                      >
                        <i className="ri-delete-bin-line" />
                      </Button>
                    </div>

                    <div className="mb-3">
                      <Label htmlFor={`question-label-${index}`}>
                        {question.type === "consent_text"
                          ? t("studio.anamnesis.builder.term_title")
                          : question.type === "section"
                            ? t("studio.anamnesis.builder.section_label")
                            : t("studio.anamnesis.builder.question_label")}
                      </Label>
                      <Input
                        id={`question-label-${index}`}
                        name={`questions.${index}.label`}
                        value={question.label}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={Boolean(getQuestionError(index, "label"))}
                      />
                      {getQuestionError(index, "label") ? (
                        <FormFeedback type="invalid">
                          {getQuestionError(index, "label")}
                        </FormFeedback>
                      ) : null}
                    </div>

                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label htmlFor={`question-type-${index}`}>
                            {t("studio.anamnesis.builder.question_type")}
                          </Label>
                          <Input
                            id={`question-type-${index}`}
                            name={`questions.${index}.type`}
                            type="select"
                            value={question.type}
                            onChange={(event) => {
                              formik.handleChange(event);
                              const nextType = event.target.value as BuilderQuestionType;
                              if (nextType === "consent_checkbox" || nextType === "signature") {
                                formik.setFieldValue(`questions.${index}.required`, true);
                              }
                              if (nextType === "section") {
                                formik.setFieldValue(`questions.${index}.required`, false);
                              }
                            }}
                            onBlur={formik.handleBlur}
                          >
                            {QUESTION_TYPES.map((type) => (
                              <option key={type} value={type}>
                                {questionTypeLabel(type)}
                              </option>
                            ))}
                          </Input>
                        </div>
                      </Col>
                      <Col md={6} className="d-flex align-items-end">
                        {question.type !== "consent_text" && question.type !== "section" ? (
                          <div className="form-check form-switch mb-3">
                            <Input
                              type="checkbox"
                              role="switch"
                              className="form-check-input"
                              id={`question-required-${index}`}
                              name={`questions.${index}.required`}
                              checked={question.required}
                              onChange={formik.handleChange}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor={`question-required-${index}`}
                            >
                              {t("studio.anamnesis.builder.required")}
                            </Label>
                          </div>
                        ) : null}
                      </Col>
                    </Row>

                    {question.type === "select" ? (
                      <div className="mb-0">
                        <Label htmlFor={`question-options-${index}`}>
                          {t("studio.anamnesis.builder.options_label")}
                        </Label>
                        <Input
                          id={`question-options-${index}`}
                          name={`questions.${index}.options`}
                          value={question.options}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={Boolean(getQuestionError(index, "options"))}
                        />
                        <div className="form-text">
                          {t("studio.anamnesis.builder.options_hint")}
                        </div>
                        {getQuestionError(index, "options") ? (
                          <FormFeedback type="invalid">
                            {getQuestionError(index, "options")}
                          </FormFeedback>
                        ) : null}
                      </div>
                    ) : null}

                    {question.type === "textarea" ? (
                      <div className="mb-0">
                        <Label htmlFor={`question-textarea-preview-${index}`}>
                          {t("studio.anamnesis.builder.textarea_preview_label")}
                        </Label>
                        <Input
                          id={`question-textarea-preview-${index}`}
                          type="textarea"
                          rows={ANAMNESIS_TEXTAREA_BUILDER_ROWS}
                          readOnly
                          tabIndex={-1}
                          className="bg-light"
                          placeholder={t("studio.anamnesis.builder.textarea_preview_placeholder")}
                        />
                        <div className="form-text">
                          {t("studio.anamnesis.builder.textarea_protocol_hint", {
                            max: ANAMNESIS_TEXTAREA_MAX_CHARS.toLocaleString(),
                          })}
                        </div>
                      </div>
                    ) : null}

                    {question.type === "section" ? (
                      <p className="text-muted small mb-0">
                        {t("studio.anamnesis.builder.section_hint")}
                      </p>
                    ) : null}

                    {question.type === "consent_text" ? (
                      <div className="mb-0">
                        {consentTerms.length > 0 ? (
                          <div className="mb-3">
                            <Label htmlFor={`question-library-${index}`}>
                              {t("studio.anamnesis.builder.library_insert")}
                            </Label>
                            <Input
                              id={`question-library-${index}`}
                              type="select"
                              defaultValue=""
                              onChange={(event) =>
                                applyLibraryTerm(index, event.target.value)
                              }
                            >
                              <option value="">
                                {t("studio.anamnesis.builder.library_placeholder")}
                              </option>
                              {consentTerms.map((term) => (
                                <option key={term.id} value={term.id}>
                                  {term.title}
                                </option>
                              ))}
                            </Input>
                          </div>
                        ) : null}
                        <Label htmlFor={`question-body-${index}`}>
                          {t("studio.anamnesis.builder.term_body")}
                        </Label>
                        <Input
                          id={`question-body-${index}`}
                          name={`questions.${index}.body`}
                          type="textarea"
                          rows={5}
                          value={question.body ?? ""}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={Boolean(getQuestionError(index, "body"))}
                        />
                        <div className="form-text">
                          {t("studio.anamnesis.builder.term_body_hint")}
                        </div>
                        {getQuestionError(index, "body") ? (
                          <FormFeedback type="invalid">
                            {getQuestionError(index, "body")}
                          </FormFeedback>
                        ) : null}
                      </div>
                    ) : null}

                    {question.type === "consent_checkbox" ? (
                      <p className="text-muted small mb-0">
                        {t("studio.anamnesis.builder.consent_checkbox_hint")}
                      </p>
                    ) : null}

                    {question.type === "signature" ? (
                      <p className="text-muted small mb-0">
                        {t("studio.anamnesis.builder.signature_hint")}
                      </p>
                    ) : null}

                    {question.type === "boolean" || question.type === "select" ? (
                      <div className="border-top pt-3 mt-3">
                        <div className="form-check form-switch mb-2">
                          <Input
                            type="checkbox"
                            role="switch"
                            className="form-check-input"
                            id={`question-clinical-gate-${index}`}
                            checked={Boolean(question.clinical_gate)}
                            onChange={(event) => {
                              formik.setFieldValue(
                                `questions.${index}.clinical_gate`,
                                event.target.checked
                              );
                              if (!event.target.checked) {
                                formik.setFieldValue(
                                  `questions.${index}.clinical_gate_service_ids`,
                                  []
                                );
                                formik.setFieldValue(
                                  `questions.${index}.clinical_gate_product_ids`,
                                  []
                                );
                              }
                            }}
                          />
                          <Label
                            className="form-check-label"
                            htmlFor={`question-clinical-gate-${index}`}
                          >
                            {t("studio.anamnesis.builder.clinical_gate_label")}
                          </Label>
                        </div>
                        <p className="text-muted small mb-2">
                          {t("studio.anamnesis.builder.clinical_gate_hint")}
                        </p>
                        {question.clinical_gate ? (
                          <>
                            <Label className="form-label">
                              {t("studio.anamnesis.builder.clinical_gate_services")}
                            </Label>
                            <div className="border rounded p-2 mb-2" style={{ maxHeight: 140, overflowY: "auto" }}>
                              {serviceOptions.length === 0 ? (
                                <p className="text-muted small mb-0">
                                  {t("studio.anamnesis.builder.clinical_gate_services_empty")}
                                </p>
                              ) : (
                                serviceOptions.map((service) => {
                                  const checked = (question.clinical_gate_service_ids ?? []).includes(
                                    service.id
                                  );
                                  return (
                                    <div className="form-check" key={`gate-svc-${index}-${service.id}`}>
                                      <Input
                                        type="checkbox"
                                        className="form-check-input"
                                        id={`gate-svc-${index}-${service.id}`}
                                        checked={checked}
                                        onChange={(event) =>
                                          toggleIdInList(
                                            `questions.${index}.clinical_gate_service_ids`,
                                            question.clinical_gate_service_ids,
                                            service.id,
                                            event.target.checked
                                          )
                                        }
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor={`gate-svc-${index}-${service.id}`}
                                      >
                                        {service.name}
                                      </Label>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                            <div className="form-text mb-3">
                              {t("studio.anamnesis.builder.clinical_gate_services_hint")}
                            </div>

                            <Label className="form-label">
                              {t("studio.anamnesis.builder.clinical_gate_products")}
                            </Label>
                            <div className="border rounded p-2 mb-0" style={{ maxHeight: 140, overflowY: "auto" }}>
                              {productOptions.length === 0 ? (
                                <p className="text-muted small mb-0">
                                  {t("studio.anamnesis.builder.clinical_gate_products_empty")}
                                </p>
                              ) : (
                                productOptions.map((product) => {
                                  const checked = (question.clinical_gate_product_ids ?? []).includes(
                                    product.id
                                  );
                                  return (
                                    <div className="form-check" key={`gate-prd-${index}-${product.id}`}>
                                      <Input
                                        type="checkbox"
                                        className="form-check-input"
                                        id={`gate-prd-${index}-${product.id}`}
                                        checked={checked}
                                        onChange={(event) =>
                                          toggleIdInList(
                                            `questions.${index}.clinical_gate_product_ids`,
                                            question.clinical_gate_product_ids,
                                            product.id,
                                            event.target.checked
                                          )
                                        }
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor={`gate-prd-${index}-${product.id}`}
                                      >
                                        {product.name}
                                      </Label>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                            <div className="form-text">
                              {t("studio.anamnesis.builder.clinical_gate_products_hint")}
                            </div>
                          </>
                        ) : null}
                      </div>
                    ) : null}
                  </CardBody>
                </Card>
              ))
            )}

            {questionsArrayError ? (
              <div className="text-danger small mb-3">{questionsArrayError}</div>
            ) : null}

            <div className="d-flex flex-column flex-sm-row gap-2">
              <Button
                color="soft-primary"
                type="button"
                className="flex-grow-1"
                onClick={() => push(emptyBuilderQuestion())}
              >
                <i className="ri-add-line align-bottom me-1" />
                {t("studio.anamnesis.builder.add_question")}
              </Button>
              <Button
                color="soft-secondary"
                type="button"
                className="flex-grow-1"
                onClick={() =>
                  push({
                    ...emptyBuilderQuestion(),
                    type: "section",
                    label: t("studio.anamnesis.builder.section_default_label"),
                  })
                }
              >
                <i className="ri-layout-row-line align-bottom me-1" />
                {t("studio.anamnesis.builder.add_section")}
              </Button>
              <Button
                color="soft-info"
                type="button"
                className="flex-grow-1"
                onClick={() =>
                  push({
                    ...emptyBuilderQuestion(),
                    type: "textarea",
                    label: t("studio.anamnesis.builder.protocol_default_label"),
                  })
                }
              >
                <i className="ri-file-list-3-line align-bottom me-1" />
                {t("studio.anamnesis.builder.add_protocol_field")}
              </Button>
            </div>
          </>
        )}
      </FieldArray>
    </div>
  );
};

export default AnamnesisTemplateBuilderFields;
